import React from 'react';
import { StaticDatePickerProps } from '@mui/x-date-pickers/StaticDatePicker';
import { PopoverContentPosition } from 'Popover/PopoverContent/types';

import CalendarRange from '../components/Calendar/CalendarRange';

export enum DatePickerVariants {
  Date = 'DATE',
  DateAndTime = 'DATE_AND_TIME',
  DateAndTimeRange = 'DATE_AND_TIME_RANGE',
  DateRange = 'DATE_RANGE',
}

export enum DateVariant {
  Date = DatePickerVariants.Date,
  DateAndTime = DatePickerVariants.DateAndTime,
}

export enum DateRangeVariant {
  DateAndTimeRange = DatePickerVariants.DateAndTimeRange,
  DateRange = DatePickerVariants.DateRange,
}

export enum InViewState {
  EndDate = 'END_DATE',
  EndTime = 'END_TIME',
  StartDate = 'START_DATE',
  StartTime = 'START_TIME',
}

export type DateTimeRange = {
  endTime?: Date | null;
  startTime?: Date | null;
};

export type SelectedTime = Date | null | undefined;

export type SelectedTimeRange = DateTimeRange | null | undefined;

export type DateProps = {
  dateFormat?: string;
  disablePast?: boolean;
  minDate?: Date | null | undefined;
  onChange: (selectedTime?: SelectedTime) => void;
  onMonthChange?: (date: Date | null | undefined) => void;
  renderDay?: StaticDatePickerProps<Date | null | undefined, Date | null | undefined>['renderDay'];
  selectedTime: SelectedTime;
  variant: DateVariant;
};

export type DateRangeProps = {
  disablePast?: boolean;
  minDate?: Date | null | undefined;
  onChange: (selectedRange?: SelectedTimeRange) => void;
  onMonthChange?: (date: Date | null | undefined) => void;
  renderDay?: StaticDatePickerProps<Date | null | undefined, Date | null | undefined>['renderDay'];
  selectedRange: SelectedTimeRange;
  variant: DateRangeVariant;
};

export type PickerProps = DateProps | DateRangeProps;

export type ActionButtons = {
  primaryText?: string;
};

export type DateInputProps = {
  disabled?: boolean;
  inViewState?: InViewState;
  onChange: (date?: Date) => void;
  onClick?: () => void;
  selectedValue: SelectedTime | SelectedTimeRange;
  setInViewState?: (InViewState: InViewState) => void;
};

export type DateInputContainerProps = DateInputProps & {
  showSelectedTime?: boolean;
  variant: DateRangeVariant | DateVariant;
};

export type DatePickerContainerProps = {
  action?: ActionButtons;
  className?: string;
  disabled?: boolean;
  error?: string;
  onPopoverClose?: () => void;
  pickerProps: PickerProps;
  shouldDisableDate?: CalendarProps['shouldDisableDate'];
  showActionButtons?: boolean;
  showSelectedTime?: boolean;
};

export type DatePickerProps = DatePickerContainerProps & {
  children?: React.ReactNode;
  input?: {
    placeholder?: string;
    size?: 'large' | 'medium';
  };
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  position?: PopoverContentPosition;
};

export type CalendarProps = {
  disabled?: boolean;
  disablePast?: boolean;
  minDate?: Date | null | undefined;
  onChange: (date: Date) => void;
  onMonthChange?: (date: Date | null | undefined) => void;
  renderDay?: StaticDatePickerProps<Date | null | undefined, Date | null | undefined>['renderDay'];
  shouldDisableDate?: StaticDatePickerProps<
    Date | null | undefined,
    Date | null | undefined
  >['shouldDisableDate'];
  value: SelectedTime;
};

export interface CalendarCompositionProps {
  Range: typeof CalendarRange;
}

export type CalendarStaticType = React.VFC<CalendarProps> & CalendarCompositionProps;

export type CalendarRangeProps = {
  disabled?: boolean;
  disablePast?: boolean;
  inViewState: InViewState;
  minDate?: Date | null | undefined;
  onChange: (date: Date) => void;
  onMonthChange?: (date: Date | null | undefined) => void;
  selectedRange: SelectedTimeRange;
  shouldDisableDate?: CalendarProps['shouldDisableDate'];
};

export type DateFieldProps = {
  autoFocus?: boolean;
  date?: Date | null;
  dateFormat?: string;
  disabled?: boolean;
  onChange: (selectedTime?: Date) => void;
  onClick?: () => void;
  placeholder?: string;
  time?: string; // "07:30"
};

export type TimeFieldProps = {
  autoFocus?: boolean;
  date?: Date | null;
  disabled?: boolean;
  onChange: (selectedTime?: Date) => void;
  onClick?: () => void;
  time?: string;
};
